import { Component, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CellRenderer } from '@shared/interfaces/table/cell-renderer';
import { TranslatePipe } from '@ngx-translate/core';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';

/**
 * Component responsible for rendering translated values in table cells.
 * Implements the CellRenderer interface for table integration.
 *
 * This component is primarily used through table column configurations to display
 * translated values based on a mapping configuration. It normalizes input values
 * to lowercase and maps them to translation keys defined in the cellRendererParams.
 *
 * ```typescript
 * {
 *   title: 'Status',
 *   key: 'status',
 *   cellRenderer: TranslateCellComponent,
 *   cellRendererParams: {
 *     'pending': 'status.pending',
 *     'approved': 'status.approved',
 *     'rejected': 'status.rejected'
 *   }
 * }
 * ```
 *
 * The component will:
 * 1. Take the value from the specified 'key' in the data
 * 2. Convert it to lowercase
 * 3. Look up the translation key in cellRendererParams
 * 4. Use TranslatePipe to display the final translated value
 */
@Component({
  selector: 'app-translate-cell',
  imports: [
    TranslatePipe,
    TooltipDirective
  ],
  templateUrl: './translate-cell.component.html',
  styleUrl: './translate-cell.component.css'
})
export class TranslateCellComponent extends CellRenderer implements OnInit, OnChanges {
  public missingConfig: boolean = false;
  // Holds the mapped translation keys to render
  public displayValues?: string[];

  /**
   * Initializes the component by processing the input value and mapping it to a translation key.
   *
   * The process includes:
   * 1. Getting the raw value either from the direct value property or from data[key]
   * 2. Converting the value to lowercase for consistent mapping
   * 3. Looking up the translation key in cellRendererParams
   *
   * @example
   * // With data: { status: 'PENDING' }
   * // and params: {
   * //   key: 'status',
   * //   cellRendererParams: { 'pending': 'status.pending' }
   * // }
   * // Will set value to: 'status.pending'
   */
  public ngOnInit(): void {
    this.fieldTranslation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.fieldTranslation();
  }

  private fieldTranslation(): void {
    // Reset state on every run to avoid stale flags/values when navigating back to the page
    this.missingConfig = false;
    this.displayValues = undefined;

    const params = this.keysToLowerCase(this.params ?? {}) as Record<string, unknown>;
    const cellRendererParams = this.keysToLowerCase(params?.['cellrendererparams'] ?? {}) as Record<string, unknown>;
    const data = this.keysToLowerCase(this.data ?? {}) as Record<string, unknown>;

    const rawKey = params?.['key'];
    const key = typeof rawKey === 'string' ? rawKey.toLowerCase() : undefined;
    if (!key) {
      this.missingConfig = true;
      return;
    }

    const rawVal = (this as any).value ?? data[key];

    if (rawVal === null || rawVal === undefined) {
      return;
    }

    let val: string | undefined;
    if (typeof rawVal === 'string') {
      val = rawVal.toLowerCase();
    } else if (typeof rawVal === 'boolean') {
      val = String(rawVal);
    }

    if (val !== undefined) {
      const parts = val.split(',').map((part) => part.trim()).filter((p) => p.length > 0);
      const values: string[] = [];
      let hasMissing = false;

      for (const part of parts) {
        if (Object.hasOwn(cellRendererParams, part)) {
          values.push(String((cellRendererParams as any)[part]));
        } else {
          hasMissing = true;
        }
      }

      this.missingConfig = hasMissing;
      this.displayValues = values.length > 0 ? values : undefined;
    } else {
      this.missingConfig = true;
    }
  }

  public keysToLowerCase<T extends Record<string, any>>(obj: T): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key.toLowerCase(), value])
    );
  }
}
